import Link from 'next/link';

import { PageInfo, PostsConnectionType } from 'utils/types';

import { formatDate } from '../../../utils/formatDate';
import Pagination from './Pagination';
import Tag from './Tag';

type ListLayoutProps = {
  description: string;
  pageInfo: PageInfo | undefined;
  pageNumber: number;
  posts: PostsConnectionType['edges'][0]['node'][];
  title: string;
  error?: string;
};

export default function ListLayout({
  description,
  pageNumber,
  title,
  posts,
  pageInfo,
  error,
}: ListLayoutProps) {
  if (error) return <p>{`Error : ${error}`}</p>;

  return (
    <>
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        <div className="space-y-2 pt-6 pb-8 md:space-y-5">
          <h1 className="text-3xl font-extrabold leading-9 tracking-tight text-gray-900 dark:text-gray-100 sm:text-4xl sm:leading-10 md:text-6xl md:leading-14">
            {title}
          </h1>
          <p className="text-lg leading-7 text-gray-500 dark:text-gray-400">
            {description}
          </p>
        </div>
        <ul className="py-8">
          {posts &&
            posts.map((post) => {
              const { slug, date, title, excerpt, tags } = post;
              return (
                <li key={slug} className="py-8">
                  <article className="space-y-2 xl:grid xl:grid-cols-4 xl:items-baseline xl:space-y-0">
                    <dl>
                      <dt className="sr-only">Published on</dt>
                      <dd className="text-base font-medium leading-6 text-gray-500 dark:text-gray-400">
                        <time dateTime={date}>{formatDate(date)}</time>
                      </dd>
                    </dl>
                    <div className="space-y-3 xl:col-span-3">
                      <div>
                        <h3 className="text-2xl font-bold leading-8 tracking-tight">
                          <Link
                            href={`/blog/${slug}`}
                            className="text-gray-900 dark:text-gray-100"
                          >
                            {title}
                          </Link>
                        </h3>
                        <div className="flex flex-wrap">
                          {tags.map((tag) => (
                            <Tag key={tag} text={tag} />
                          ))}
                        </div>
                      </div>
                      <div className="prose max-w-none text-gray-500 dark:text-gray-400">
                        {excerpt}
                      </div>
                      <div className="text-base leading-6 font-medium text-black dark:text-white hover:text-opacity-80">
                        <Link
                          href={`/blog/${post.slug}`}
                          aria-label={`Read ${post.title}"`}
                        >
                          Read more &rarr;
                        </Link>
                      </div>
                    </div>
                  </article>
                </li>
              );
            })}
        </ul>
      </div>
      {pageInfo && (
        <div className="flex justify-center">
          <Pagination currentPage={pageNumber} pageInfo={pageInfo} />
        </div>
      )}
    </>
  );
}
